import React, { useState, useEffect } from 'react';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';
import { Card, Btn as Button, Badge, Modal, Tabs, MetricCard } from '../../components/ui';
import { Icon } from '../../icons';
import axios from '../../api/axios';
import toast from 'react-hot-toast';

const translations = { en, hi, gu };

const SecurityGateSearch = () => {
    const { language = 'en' } = useSettings();
    const t = (key, params, ns = 'gate_search') => {
        let str = translations[language]?.[ns]?.[key] || translations.en?.[ns]?.[key] || key;
        if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
        return str;
    };

    // Active View Tab: 0 = Gate Entry / Lookup, 1 = Visitor & Security Logs, 2 = Reports & Analytics
    const [activeTab, setActiveTab] = useState(0);

    const [searchTerm, setSearchTerm] = useState('');
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);

    // Remarks / Entry modal state for normal check-in
    const [checkInProvider, setCheckInProvider] = useState(null);
    const [checkInRemarks, setCheckInRemarks] = useState('');

    // Logs State
    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [logSearch, setLogSearch] = useState('');
    const [logStatusFilter, setLogStatusFilter] = useState('');
    const [logDateFilter, setLogDateFilter] = useState('');
    const [logsSummary, setLogsSummary] = useState({
        today_total_visits: 0,
        today_currently_inside: 0,
        today_completed_visits: 0,
    });
    const [logsPagination, setLogsPagination] = useState({ current_page: 1, last_page: 1, total: 0 });

    // Reports State
    const [reportType, setReportType] = useState('daily'); // 'daily' | 'monthly' | 'provider'
    const [reportDate, setReportDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [reportMonth, setReportMonth] = useState(() => new Date().getMonth() + 1);
    const [reportYear, setReportYear] = useState(() => new Date().getFullYear());
    const [reportProviderId, setReportProviderId] = useState('');
    const [allProvidersList, setAllProvidersList] = useState([]);
    const [reportData, setReportData] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    const [recentSearches, setRecentSearches] = useState(() => {
        try {
            const saved = localStorage.getItem('gate_provider_recent_searches');
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            return [];
        }
    });
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [overrideProvider, setOverrideProvider] = useState(null);
    const [overrideReason, setOverrideReason] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [searchedTerm, setSearchedTerm] = useState('');

    const saveRecentSearch = (term) => {
        if (!term || !term.trim()) return;
        const trimmed = term.trim();
        const updated = [trimmed, ...recentSearches.filter(s => s.toLowerCase() !== trimmed.toLowerCase())].slice(0, 100);
        setRecentSearches(updated);
        try {
            localStorage.setItem('gate_provider_recent_searches', JSON.stringify(updated));
        } catch (e) {
            console.error('Failed to save recent search to localStorage', e);
        }
    };

    const clearRecentSearches = () => {
        setRecentSearches([]);
        try {
            localStorage.removeItem('gate_provider_recent_searches');
        } catch (e) {}
    };

    const handleSearch = async (termToSearch) => {
        const query = (typeof termToSearch === 'string' ? termToSearch : searchTerm).trim();
        if (!query) return;
        if (typeof termToSearch === 'string') {
            setSearchTerm(query);
        }
        setSearchedTerm(query);
        saveRecentSearch(query);
        setLoading(true);
        setHasSearched(true);
        try {
            const response = await axios.get('/api/v1/gate-visitor-service-providers/search', {
                params: { search: query }
            });
            setProviders(response.data || []);
            saveRecentSearch(termToSearch);
        } catch (error) {
            console.error('Error searching:', error);
            toast.error(error.response?.data?.message || 'Search failed');
        }
        setLoading(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const fetchLogs = async (page = 1) => {
        setLogsLoading(true);
        try {
            const res = await axios.get('/api/v1/gate-visitor-service-providers/logs', {
                params: {
                    page,
                    search: logSearch.trim() || undefined,
                    status: logStatusFilter || undefined,
                    date: logDateFilter || undefined,
                }
            });
            const data = res.data || {};
            setLogs(data.logs?.data || []);
            setLogsSummary(data.summary || {
                today_total_visits: 0,
                today_currently_inside: 0,
                today_completed_visits: 0,
            });
            setLogsPagination({
                current_page: data.logs?.current_page || 1,
                last_page: data.logs?.last_page || 1,
                total: data.logs?.total || 0,
            });
        } catch (error) {
            console.error('Error fetching logs:', error);
            toast.error(error.response?.data?.message || 'Failed to load logs');
        } finally {
            setLogsLoading(false);
        }
    };

    const fetchAllProviders = async () => {
        try {
            const res = await axios.get('/api/v1/visitor-service-providers');
            const list = res.data?.data || res.data || [];
            setAllProvidersList(list);
            return list;
        } catch (e) {
            return [];
        }
    };

    const generateReport = async (overrideProvId, explicitClick = false) => {
        const provIdToUse = overrideProvId !== undefined ? overrideProvId : reportProviderId;
        setReportLoading(true);
        try {
            if (reportType === 'daily') {
                const res = await axios.get('/api/v1/reports/security-gate/daily', {
                    params: { date: reportDate }
                });
                setReportData(res.data || null);
            } else if (reportType === 'monthly') {
                const res = await axios.get('/api/v1/reports/security-gate/monthly', {
                    params: { month: reportMonth, year: reportYear }
                });
                setReportData(res.data || null);
            } else if (reportType === 'provider') {
                if (!provIdToUse) {
                    if (explicitClick) {
                        toast.error('Please select a service provider first');
                    }
                    setReportData(null);
                    setReportLoading(false);
                    return;
                }
                const res = await axios.get(`/api/v1/reports/security-gate/provider/${provIdToUse}`);
                setReportData(res.data || null);
            }
        } catch (error) {
            console.error('Error generating report:', error);
            if (explicitClick) {
                toast.error(error.response?.data?.message || 'Failed to generate report');
            }
        } finally {
            setReportLoading(false);
        }
    };

    const handleExport = async (format = 'excel') => {
        if (reportType === 'provider' && !reportProviderId) {
            toast.error('Please select a service provider first');
            return;
        }
        setExportLoading(true);
        try {
            const endpoint = format === 'pdf' 
                ? '/api/v1/reports/security-gate/export/pdf'
                : '/api/v1/reports/security-gate/export/excel';

            const params = {
                type: reportType,
                date: reportType === 'daily' ? reportDate : undefined,
                month: reportType === 'monthly' ? reportMonth : undefined,
                year: reportType === 'monthly' ? reportYear : undefined,
                provider_id: reportType === 'provider' ? reportProviderId : undefined,
            };

            const response = await axios.get(endpoint, {
                params,
                responseType: 'blob'
            });

            const blob = new Blob([response.data], {
                type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `gate_report_${reportType}_${Date.now()}.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success(`${format.toUpperCase()} downloaded successfully`);
        } catch (error) {
            console.error(`Export ${format} error:`, error);
            toast.error(`Failed to export ${format.toUpperCase()}`);
        } finally {
            setExportLoading(false);
        }
    };

    // Load logs or providers when tab changes
    useEffect(() => {
        if (activeTab === 1) {
            fetchLogs(1);
        } else if (activeTab === 2) {
            fetchAllProviders().then((list) => {
                if (reportType === 'provider') {
                    if (!reportProviderId && list && list.length > 0) {
                        const firstId = list[0].id;
                        setReportProviderId(firstId);
                        generateReport(firstId, false);
                    } else if (reportProviderId) {
                        generateReport(reportProviderId, false);
                    }
                } else {
                    generateReport(undefined, false);
                }
            });
        }
    }, [activeTab, logStatusFilter, logDateFilter, reportType]);

    const handleCheckIn = async (provider, isOverride = false, remarks = '') => {
        setActionLoading(true);
        try {
            await axios.post('/api/v1/gate-visitor-service-providers/entry', { 
                provider_id: provider.id,
                is_override: isOverride,
                remarks: remarks
            });
            toast.success(t("toast_checked_in"));
            setOverrideProvider(null);
            setOverrideReason('');
            setCheckInProvider(null);
            setCheckInRemarks('');
            if (activeTab === 0) {
                handleSearch(); // Refresh list to get latest entry and access status
            } else {
                fetchLogs(1);
            }
        } catch (error) {
            const msg = error.response?.data?.message || error.message;
            toast.error(msg);
        } finally {
            setActionLoading(false);
        }
    };

    const handleCheckOut = async (provider) => {
        setActionLoading(true);
        try {
            const activeEntryId = provider.active_entry?.id;
            const url = activeEntryId 
                ? `/api/v1/gate-visitor-service-providers/exit/${activeEntryId}`
                : `/api/v1/gate-visitor-service-providers/exit`;
            
            await axios.post(url, { provider_id: provider.id });
            toast.success(t("toast_checked_out"));
            if (activeTab === 0) {
                handleSearch(); // Refresh to update check-out status
            } else {
                fetchLogs(logsPagination.current_page);
            }
        } catch (error) {
            const msg = error.response?.data?.message || error.message;
            toast.error(msg);
        } finally {
            setActionLoading(false);
        }
    };

    const handleConfirmOverride = () => {
        if (!overrideReason.trim()) {
            toast.error(t("toast_reason_required"));
            return;
        }
        handleCheckIn(overrideProvider, true, overrideReason.trim());
    };

    return (
        <div className="space-y-4 sm:space-y-5 fade-up text-ink dark:text-white">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-ink dark:text-white">
                        {t("title_security_gate")}
                    </h1>
                    <p className="text-xs text-muted dark:text-slate-400 mt-0.5">
                        Manage real-time check-in/out, manual overrides, and visitor security audit logs
                    </p>
                </div>
            </div>

            {/* Navigation Tabs */}
            <Tabs
                tabs={[
                    { label: t("tab_gate_entry") },
                    { label: t("tab_visitor_logs") },
                    { label: t("tab_reports") }
                ]}
                active={activeTab}
                onChange={setActiveTab}
                scrollable
            />

            {/* Daily Visit Count Summary Cards */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 lg:gap-4">
                <MetricCard
                    accent="blue"
                    icon="activity"
                    label={t("stat_today_total")}
                    value={logsSummary.today_total_visits}
                    loading={logsLoading && activeTab === 1}
                />
                <MetricCard
                    accent="cyan"
                    icon="users"
                    label={t("stat_currently_inside")}
                    value={logsSummary.today_currently_inside}
                    loading={logsLoading && activeTab === 1}
                />
                <MetricCard
                    accent="green"
                    icon="check"
                    label={t("stat_completed_visits")}
                    value={logsSummary.today_completed_visits}
                    loading={logsLoading && activeTab === 1}
                />
            </div>

            {/* TAB 0: GATE ENTRY & LOOKUP */}
            {activeTab === 0 && (
                <div className="space-y-4 sm:space-y-5">
                    <Card className="p-3 sm:p-4">
                        <div className="flex gap-2 sm:gap-3">
                            <div className="relative flex-1">
                                <input
                                    placeholder={t("placeholder_search")}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    className="w-full h-10 px-3 pl-9 border border-border dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm text-ink dark:text-white placeholder:text-muted transition-colors"
                                />
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted">
                                    <Icon name="search" size={14} />
                                </span>
                                {searchTerm && (
                                    <button
                                        type="button"
                                        onClick={() => setSearchTerm('')}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-ink dark:hover:text-white text-xs font-semibold"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                            <Button 
                                variant="primary"
                                onClick={() => handleSearch()} 
                                disabled={loading || !searchTerm.trim()}
                                className="h-10 px-4 sm:px-6 shrink-0"
                            >
                                {loading ? t("btn_searching") : t("btn_search")}
                            </Button>
                        </div>

                        {/* Recent Searches */}
                        {recentSearches.length > 0 && (
                            <div className="flex items-center flex-wrap gap-1.5 pt-2.5 mt-2.5 border-t border-border/60 text-xs text-muted">
                                <span className="font-semibold text-ink dark:text-slate-300 flex items-center gap-1">
                                    <Icon name="clock" size={12} /> {t("recent_searches")}:
                                </span>
                                {recentSearches.slice(0, 3).map((item, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        onClick={() => handleSearch(item)}
                                        className="px-2.5 py-0.5 bg-slate-100 dark:bg-slate-800 hover:bg-primary/10 hover:text-primary dark:hover:bg-primary/20 dark:hover:text-primary-light rounded-full border border-border dark:border-slate-700 transition-colors cursor-pointer text-xs"
                                    >
                                        {item}
                                    </button>
                                ))}
                                {recentSearches.length > 3 && (
                                    <button
                                        type="button"
                                        onClick={() => setShowHistoryModal(true)}
                                        className="px-1.5 py-0.5 text-xs text-primary hover:underline font-medium cursor-pointer"
                                    >
                                        {t("view_all", { count: recentSearches.length })}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={clearRecentSearches}
                                    className="text-xs text-red-500 hover:underline ml-auto cursor-pointer font-medium"
                                >
                                    {t("clear_history")}
                                </button>
                            </div>
                        )}
                    </Card>

                    <div className="space-y-4">
                        {providers.map(provider => {
                            const isInside = !!provider.active_entry;
                            const status = provider.access_status?.status || (provider.access_status?.allowed ? 'Active' : (!provider.is_active ? 'Blocked' : 'Expired'));
                            const statusColor = isInside ? 'blue' : (status === 'Active' ? 'green' : (status === 'Blocked' ? 'red' : 'amber'));
                            const statusKey = isInside ? 'status_inside' : (status === 'Active' ? 'status_active' : (status === 'Blocked' ? 'status_blocked' : 'status_expired'));

                            const lastEntryTime = provider.latest_entry?.entry_time 
                                ? new Date(provider.latest_entry.entry_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                : null;
                            const lastExitTime = provider.latest_entry?.exit_time 
                                ? new Date(provider.latest_entry.exit_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                : null;
                            const lastEntryDate = provider.latest_entry?.entry_time 
                                ? new Date(provider.latest_entry.entry_time).toLocaleDateString() 
                                : null;

                            const canNormalCheckIn = provider.access_status?.allowed && !isInside;

                            return (
                                <Card key={provider.id} className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 sm:gap-4 hover:shadow-card-hover transition-all">
                                    <div className="flex items-start gap-3 sm:gap-4 min-w-0 flex-1">
                                        {provider.photo_url ? (
                                            <img 
                                                src={provider.photo_url} 
                                                alt={provider.name} 
                                                className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-primary/30 cursor-pointer hover:opacity-80 transition-opacity shrink-0"
                                                onClick={() => setSelectedPhoto(provider.photo_url)}
                                            />
                                        ) : (
                                            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-slate-100 dark:bg-slate-800 border border-border dark:border-slate-700 flex items-center justify-center text-lg sm:text-xl font-bold shrink-0 text-slate-600 dark:text-slate-300">
                                                {provider.name.charAt(0)}
                                            </div>
                                        )}
                                        <div className="space-y-1 min-w-0 flex-1">
                                            <div className="flex items-center flex-wrap gap-1.5 sm:gap-2">
                                                <h3 className="font-bold text-base sm:text-lg text-ink dark:text-white truncate">
                                                    {provider.name}
                                                </h3>
                                                {/* Provider ID */}
                                                <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 border border-border dark:border-slate-700 text-muted font-mono font-medium">
                                                    #{provider.id}
                                                </span>
                                                {/* Access Status: Active, Expired, Blocked, or Currently Inside */}
                                                <Badge color={statusColor} dot>
                                                    {t(statusKey)}
                                                </Badge>
                                            </div>

                                            <p className="text-muted dark:text-slate-400 capitalize text-xs sm:text-sm">
                                                {t("type_" + provider.service_type.toLowerCase().replace(/ /g, "_"), null, "service_providers")} • {provider.mobile_number}
                                            </p>
                                            
                                            {/* Assigned Unit/Flat & Resident Name */}
                                            {provider.units && provider.units.length > 0 && (
                                                <div className="pt-0.5 flex flex-col gap-1 text-xs text-muted dark:text-slate-300">
                                                    {provider.units.map((u, i) => {
                                                        const unitLabel = u.unit ? `${u.unit.block ? u.unit.block + '-' : ''}${u.unit.unit_number}` : `Unit #${u.unit_id}`;
                                                        const residentName = u.member?.name || u.unit?.tenant_name || u.unit?.owner_name || u.unit?.users?.[0]?.name || null;

                                                        return (
                                                            <div key={i} className="flex items-center flex-wrap gap-1.5">
                                                                <span className="font-medium text-slate-500 dark:text-slate-400">{t("assigned_units")}:</span>
                                                                <span className="bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded font-semibold text-ink dark:text-slate-200 border border-border dark:border-slate-700">
                                                                    {unitLabel}
                                                                </span>
                                                                {residentName && (
                                                                    <span className="text-muted dark:text-slate-400 flex items-center gap-1 ml-1">
                                                                        • <span className="font-medium">{t("resident_name")}:</span>
                                                                        <span className="text-ink dark:text-slate-300 font-medium">{residentName}</span>
                                                                    </span>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {/* Last Check-in / Check-out timing info */}
                                            {lastEntryTime && (
                                                <div className="flex items-center flex-wrap gap-2 text-xs text-muted dark:text-slate-400 pt-0.5">
                                                    <span className="inline-flex items-center gap-1 font-medium">
                                                        ⏱️ {t("last_check_in")}: <span className="text-ink dark:text-slate-300 font-semibold">{lastEntryTime} ({lastEntryDate})</span>
                                                    </span>
                                                    {lastExitTime ? (
                                                        <span className="inline-flex items-center gap-1">
                                                            • {t("last_check_out")}: <span className="text-ink dark:text-slate-300 font-semibold">{lastExitTime}</span>
                                                        </span>
                                                    ) : isInside && (
                                                        <span className="text-emerald-600 dark:text-emerald-400 font-semibold bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded border border-emerald-200 dark:border-emerald-800">
                                                            {t("currently_inside_since", { time: lastEntryTime })}
                                                        </span>
                                                    )}
                                                </div>
                                            )}

                                            {provider.access_status?.reason && (
                                                <p className="text-xs text-muted dark:text-slate-500 italic pt-0.5">
                                                    {provider.access_status.reason}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Actions: Check-in / Check-out / Override */}
                                    <div className="w-full md:w-auto flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0 pt-2.5 md:pt-0 border-t border-border/60 md:border-t-0">
                                        {isInside ? (
                                            <Button
                                                variant="danger"
                                                loading={actionLoading}
                                                onClick={() => handleCheckOut(provider)}
                                                className="w-full sm:w-auto h-9 sm:h-10"
                                            >
                                                {t("btn_check_out")}
                                            </Button>
                                        ) : canNormalCheckIn ? (
                                            <Button
                                                variant="solid_success"
                                                loading={actionLoading}
                                                onClick={() => {
                                                    setCheckInProvider(provider);
                                                    setCheckInRemarks('');
                                                }}
                                                className="w-full sm:w-auto h-9 sm:h-10"
                                            >
                                                {t("btn_check_in")}
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="secondary_outline"
                                                loading={actionLoading}
                                                onClick={() => {
                                                    setOverrideProvider(provider);
                                                    setOverrideReason('');
                                                }}
                                                className="w-full sm:w-auto h-9 sm:h-10"
                                            >
                                                {t("btn_manual_override")}
                                            </Button>
                                        )}
                                    </div>
                                </Card>
                            );
                        })}

                        {/* Empty State: No Providers Found */}
                        {providers.length === 0 && !loading && searchedTerm && (
                            <Card className="p-10 text-center flex flex-col items-center justify-center border-dashed border-2 border-slate-300 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/30">
                                <div className="w-16 h-16 rounded-full bg-rose-50 dark:bg-rose-950/40 text-rose-500 flex items-center justify-center text-3xl mb-4 shadow-inner">
                                    🔍
                                </div>
                                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-1">
                                    {t("msg_no_providers")}
                                </h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md">
                                    {t("msg_no_providers_sub", { term: searchedTerm })}
                                </p>
                            </Card>
                        )}
                    </div>
                </div>
            )}

            {/* TAB 1: VISITOR & SECURITY LOGS REVIEW */}
            {activeTab === 1 && (
                <div className="space-y-4">
                    {/* Filter bar */}
                    <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 items-stretch sm:items-center justify-between">
                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto flex-1">
                            <div className="relative flex-1 sm:w-64 min-w-[160px]">
                                <input
                                    type="text"
                                    value={logSearch}
                                    onChange={(e) => setLogSearch(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && fetchLogs(1)}
                                    placeholder={t("placeholder_filter_logs")}
                                    className="w-full h-9 px-3 pl-8 text-xs rounded-xl border border-border dark:border-slate-700 bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/20 text-ink dark:text-white"
                                />
                                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted">
                                    <Icon name="search" size={13} />
                                </span>
                            </div>
                            <select
                                value={logStatusFilter}
                                onChange={(e) => setLogStatusFilter(e.target.value)}
                                className="h-9 px-2.5 text-xs rounded-xl border border-border dark:border-slate-700 bg-white dark:bg-slate-800 text-ink dark:text-slate-200 focus:outline-none cursor-pointer"
                            >
                                <option value="">{t("filter_all_status")}</option>
                                <option value="inside">{t("filter_inside_only")}</option>
                                <option value="exited">{t("filter_exited_only")}</option>
                            </select>
                            <input
                                type="date"
                                value={logDateFilter}
                                onChange={(e) => setLogDateFilter(e.target.value)}
                                className="h-9 px-2.5 text-xs rounded-xl border border-border dark:border-slate-700 bg-white dark:bg-slate-800 text-ink dark:text-slate-200 focus:outline-none cursor-pointer"
                            />
                        </div>
                        <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                            <Button 
                                variant="secondary" 
                                sm 
                                icon="refresh" 
                                loading={logsLoading} 
                                onClick={() => fetchLogs(logsPagination.current_page)}
                                className="h-9"
                            >
                                {t("btn_refresh")}
                            </Button>
                        </div>
                    </div>

                    {/* Logs Table / Cards */}
                    <Card noPad className="overflow-hidden border border-border dark:border-slate-800">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs">
                                <thead className="bg-slate-50 dark:bg-slate-800/80 text-slate-500 dark:text-slate-400 font-semibold border-b border-slate-200 dark:border-slate-800">
                                    <tr>
                                        <th className="p-3.5">{t("col_provider_visitor")}</th>
                                        <th className="p-3.5">{t("col_unit_resident")}</th>
                                        <th className="p-3.5">{t("col_entry_time")}</th>
                                        <th className="p-3.5">{t("col_exit_time")}</th>
                                        <th className="p-3.5">{t("col_approved_by")}</th>
                                        <th className="p-3.5">{t("col_remarks")}</th>
                                        <th className="p-3.5 text-right">{t("col_status")}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                                    {logs.map((log) => {
                                        const isStillInside = !log.exit_time;
                                        const entryFormatted = log.entry_time 
                                            ? new Date(log.entry_time).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
                                            : '-';
                                        const exitFormatted = log.exit_time 
                                            ? new Date(log.exit_time).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
                                            : '-';
                                        const unitNumber = log.unit ? `${log.unit.block ? log.unit.block + '-' : ''}${log.unit.unit_number}` : '-';

                                        return (
                                            <tr key={log.id} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors">
                                                {/* Provider info */}
                                                <td className="p-3.5">
                                                    <div className="font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                                                        <span>{log.provider?.name || 'Unknown Provider'}</span>
                                                        <span className="text-[10px] font-mono text-slate-400">#{log.visitor_service_provider_id}</span>
                                                    </div>
                                                    <div className="text-[11px] text-slate-400">
                                                        {log.provider?.service_type} • {log.provider?.mobile_number}
                                                    </div>
                                                </td>
                                                {/* Unit info */}
                                                <td className="p-3.5">
                                                    <span className="font-medium text-slate-700 dark:text-slate-300">
                                                        {unitNumber}
                                                    </span>
                                                </td>
                                                {/* Entry Time */}
                                                <td className="p-3.5 font-mono text-slate-600 dark:text-slate-300">
                                                    {entryFormatted}
                                                </td>
                                                {/* Exit Time */}
                                                <td className="p-3.5 font-mono text-slate-600 dark:text-slate-300">
                                                    {isStillInside ? (
                                                        <span className="text-amber-500 font-sans italic">Inside</span>
                                                    ) : (
                                                        exitFormatted
                                                    )}
                                                </td>
                                                {/* Approved By Security Guard */}
                                                <td className="p-3.5">
                                                    <span className="inline-flex items-center gap-1 text-slate-700 dark:text-slate-300 font-medium">
                                                        <Icon name="shield" size={12} className="text-primary" />
                                                        {log.security_user?.name || 'Security Guard'}
                                                    </span>
                                                </td>
                                                {/* Remarks / Override Reason */}
                                                <td className="p-3.5 max-w-xs truncate text-slate-500 dark:text-slate-400">
                                                    {log.remarks ? (
                                                        <span title={log.remarks} className="bg-slate-100 dark:bg-slate-800/80 px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700">
                                                            {log.remarks}
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-300 dark:text-slate-600">-</span>
                                                    )}
                                                </td>
                                                {/* Status Badge & Check-out action if inside */}
                                                <td className="p-3.5 text-right whitespace-nowrap">
                                                    {isStillInside ? (
                                                        <div className="flex items-center justify-end gap-2">
                                                            <Badge color="blue" dot sm>
                                                                {t("status_inside")}
                                                            </Badge>
                                                            <Button
                                                                variant="danger"
                                                                xs
                                                                loading={actionLoading}
                                                                onClick={() => handleCheckOut(log.provider || { id: log.visitor_service_provider_id, active_entry: log })}
                                                            >
                                                                {t("btn_check_out")}
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <Badge color="green" sm>
                                                            {t("stat_completed_visits")}
                                                        </Badge>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {logs.length === 0 && !logsLoading && (
                            <div className="p-8 text-center text-slate-400 text-xs">
                                {t("no_logs_found")}
                            </div>
                        )}
                    </Card>

                    {/* Pagination */}
                    {logsPagination.last_page > 1 && (
                        <div className="flex justify-between items-center text-xs text-slate-500 pt-2">
                            <span>
                                Page {logsPagination.current_page} of {logsPagination.last_page} ({logsPagination.total} entries)
                            </span>
                            <div className="flex gap-1">
                                <Button
                                    variant="secondary"
                                    xs
                                    disabled={logsPagination.current_page <= 1}
                                    onClick={() => fetchLogs(logsPagination.current_page - 1)}
                                >
                                    Previous
                                </Button>
                                <Button
                                    variant="secondary"
                                    xs
                                    disabled={logsPagination.current_page >= logsPagination.last_page}
                                    onClick={() => fetchLogs(logsPagination.current_page + 1)}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* TAB 2: REPORTS & ANALYTICS */}
            {activeTab === 2 && (
                <div className="space-y-4 sm:space-y-5">
                    {/* Report Type Selector & Action Bar */}
                    <Card className="space-y-3 sm:space-y-4">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 sm:gap-4">
                            <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                <button
                                    type="button"
                                    onClick={() => setReportType('daily')}
                                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer border ${reportType === 'daily' ? 'bg-primary text-white border-primary shadow-xs' : 'bg-slate-50 dark:bg-slate-800 text-ink dark:text-slate-300 border-border dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                                >
                                    📅 {t("report_daily_entry")}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setReportType('monthly')}
                                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer border ${reportType === 'monthly' ? 'bg-primary text-white border-primary shadow-xs' : 'bg-slate-50 dark:bg-slate-800 text-ink dark:text-slate-300 border-border dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                                >
                                    📊 {t("report_monthly_attendance")}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setReportType('provider')}
                                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer border ${reportType === 'provider' ? 'bg-primary text-white border-primary shadow-xs' : 'bg-slate-50 dark:bg-slate-800 text-ink dark:text-slate-300 border-border dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                                >
                                    👤 {t("report_provider_history")}
                                </button>
                            </div>

                            {/* Export Buttons */}
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="secondary"
                                    sm
                                    icon="download"
                                    loading={exportLoading}
                                    onClick={() => handleExport('excel')}
                                >
                                    {t("btn_export_excel")}
                                </Button>
                                <Button
                                    variant="danger"
                                    sm
                                    icon="file"
                                    loading={exportLoading}
                                    onClick={() => handleExport('pdf')}
                                >
                                    {t("btn_export_pdf")}
                                </Button>
                            </div>
                        </div>

                        {/* Filter criteria controls */}
                        <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex flex-wrap items-center gap-3">
                            {reportType === 'daily' && (
                                <div className="flex items-center gap-2 text-xs">
                                    <label className="font-semibold text-slate-600 dark:text-slate-300">{t("label_select_date")}:</label>
                                    <input
                                        type="date"
                                        value={reportDate}
                                        onChange={(e) => setReportDate(e.target.value)}
                                        className="p-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-slate-900 dark:text-white cursor-pointer"
                                    />
                                </div>
                            )}

                            {reportType === 'monthly' && (
                                <div className="flex items-center gap-3 text-xs">
                                    <div className="flex items-center gap-1.5">
                                        <label className="font-semibold text-slate-600 dark:text-slate-300">{t("label_select_month")}:</label>
                                        <select
                                            value={reportMonth}
                                            onChange={(e) => setReportMonth(Number(e.target.value))}
                                            className="p-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-slate-900 dark:text-white cursor-pointer"
                                        >
                                            {Array.from({ length: 12 }, (_, i) => (
                                                <option key={i + 1} value={i + 1}>
                                                    {new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <label className="font-semibold text-slate-600 dark:text-slate-300">{t("label_select_year")}:</label>
                                        <input
                                            type="number"
                                            value={reportYear}
                                            onChange={(e) => setReportYear(Number(e.target.value))}
                                            className="w-20 p-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-slate-900 dark:text-white"
                                        />
                                    </div>
                                </div>
                            )}

                            {reportType === 'provider' && (
                                <div className="flex items-center gap-2 text-xs flex-1 max-w-md">
                                    <label className="font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">{t("label_select_provider")}:</label>
                                    <select
                                        value={reportProviderId}
                                        onChange={(e) => setReportProviderId(e.target.value)}
                                        className="w-full p-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-slate-900 dark:text-white cursor-pointer"
                                    >
                                        <option value="">-- Choose Provider --</option>
                                        {allProvidersList.map(p => (
                                            <option key={p.id} value={p.id}>
                                                #{p.id} - {p.name} ({p.service_type})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <Button
                                variant="primary"
                                sm
                                icon="refresh"
                                loading={reportLoading}
                                onClick={() => generateReport(undefined, true)}
                            >
                                {t("btn_generate_report")}
                            </Button>
                        </div>
                    </Card>

                    {/* Report Summary Cards */}
                    {reportData?.summary && (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {reportType === 'daily' && (
                                <>
                                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                                        <div className="text-[10px] uppercase font-bold text-slate-400">Total Entries</div>
                                        <div className="text-xl font-extrabold text-slate-900 dark:text-white mt-0.5">{reportData.summary.total_entries}</div>
                                    </div>
                                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                                        <div className="text-[10px] uppercase font-bold text-slate-400">Completed Visits</div>
                                        <div className="text-xl font-extrabold text-emerald-600 dark:text-emerald-400 mt-0.5">{reportData.summary.completed_visits}</div>
                                    </div>
                                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                                        <div className="text-[10px] uppercase font-bold text-slate-400">Currently Inside</div>
                                        <div className="text-xl font-extrabold text-sky-600 dark:text-sky-400 mt-0.5">{reportData.summary.currently_inside}</div>
                                    </div>
                                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                                        <div className="text-[10px] uppercase font-bold text-slate-400">{t("stat_unique_providers")}</div>
                                        <div className="text-xl font-extrabold text-indigo-600 dark:text-indigo-400 mt-0.5">{reportData.summary.unique_providers}</div>
                                    </div>
                                </>
                            )}

                            {reportType === 'monthly' && (
                                <>
                                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 col-span-2">
                                        <div className="text-[10px] uppercase font-bold text-slate-400">{t("stat_total_monthly_visits")}</div>
                                        <div className="text-xl font-extrabold text-indigo-600 dark:text-indigo-400 mt-0.5">{reportData.summary.total_visits_in_month}</div>
                                    </div>
                                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 col-span-2">
                                        <div className="text-[10px] uppercase font-bold text-slate-400">{t("stat_active_providers")}</div>
                                        <div className="text-xl font-extrabold text-emerald-600 dark:text-emerald-400 mt-0.5">{reportData.summary.total_active_providers}</div>
                                    </div>
                                </>
                            )}

                            {reportType === 'provider' && (
                                <>
                                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                                        <div className="text-[10px] uppercase font-bold text-slate-400">Total Visits Recorded</div>
                                        <div className="text-xl font-extrabold text-primary mt-0.5">{reportData.summary.total_visits}</div>
                                    </div>
                                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                                        <div className="text-[10px] uppercase font-bold text-slate-400">Provider Service</div>
                                        <div className="text-sm font-bold text-slate-800 dark:text-slate-200 mt-1 capitalize">{reportData.summary.service_type}</div>
                                    </div>
                                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                                        <div className="text-[10px] uppercase font-bold text-slate-400">{t("col_first_visit")}</div>
                                        <div className="text-xs font-semibold text-slate-700 dark:text-slate-300 mt-1">{reportData.summary.first_recorded_visit || 'N/A'}</div>
                                    </div>
                                    <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                                        <div className="text-[10px] uppercase font-bold text-slate-400">{t("col_last_visit")}</div>
                                        <div className="text-xs font-semibold text-slate-700 dark:text-slate-300 mt-1">{reportData.summary.last_recorded_visit || 'N/A'}</div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Report Data Presentation Table */}
                    <Card noPad className="overflow-hidden border border-border dark:border-slate-800">
                        <div className="overflow-x-auto">
                            {/* Monthly Attendance Table View */}
                            {reportType === 'monthly' ? (
                                <table className="w-full text-left text-xs">
                                    <thead className="bg-slate-50 dark:bg-slate-800/80 text-slate-500 dark:text-slate-400 font-semibold border-b border-slate-200 dark:border-slate-800">
                                        <tr>
                                            <th className="p-3.5">Provider ID</th>
                                            <th className="p-3.5">Provider Name</th>
                                            <th className="p-3.5">Service Type</th>
                                            <th className="p-3.5">Contact Number</th>
                                            <th className="p-3.5">{t("col_days_present")}</th>
                                            <th className="p-3.5">{t("col_total_entries")}</th>
                                            <th className="p-3.5 text-right">{t("col_last_seen")}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                                        {(reportData?.attendance || []).map((row, idx) => (
                                            <tr key={idx} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40">
                                                <td className="p-3.5 font-mono text-slate-500">#{row.provider_id}</td>
                                                <td className="p-3.5 font-semibold text-slate-800 dark:text-slate-100">{row.provider_name}</td>
                                                <td className="p-3.5 capitalize text-slate-600 dark:text-slate-300">{row.service_type}</td>
                                                <td className="p-3.5 text-slate-500 dark:text-slate-400">{row.mobile_number}</td>
                                                <td className="p-3.5">
                                                    <span className="font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50 px-2 py-0.5 rounded border border-emerald-200 dark:border-emerald-800">
                                                        {row.days_attended} Days
                                                    </span>
                                                </td>
                                                <td className="p-3.5 font-semibold">{row.total_visits} Visits</td>
                                                <td className="p-3.5 text-right font-mono text-slate-500">{row.last_visit || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                /* Daily / Provider Entry Logs Table View */
                                <table className="w-full text-left text-xs">
                                    <thead className="bg-slate-50 dark:bg-slate-800/80 text-slate-500 dark:text-slate-400 font-semibold border-b border-slate-200 dark:border-slate-800">
                                        <tr>
                                            <th className="p-3.5">{t("col_provider_visitor")}</th>
                                            <th className="p-3.5">{t("col_unit_resident")}</th>
                                            <th className="p-3.5">{t("col_entry_time")}</th>
                                            <th className="p-3.5">{t("col_exit_time")}</th>
                                            <th className="p-3.5">{t("col_approved_by")}</th>
                                            <th className="p-3.5">{t("col_remarks")}</th>
                                            <th className="p-3.5 text-right">{t("col_status")}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                                        {(reportData?.entries || []).map((entry) => {
                                            const isInside = !entry.exit_time;
                                            const entryFormatted = entry.entry_time 
                                                ? new Date(entry.entry_time).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
                                                : '-';
                                            const exitFormatted = entry.exit_time 
                                                ? new Date(entry.exit_time).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
                                                : '-';
                                            const unitNumber = entry.unit ? `${entry.unit.block ? entry.unit.block + '-' : ''}${entry.unit.unit_number}` : '-';

                                            return (
                                                <tr key={entry.id} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40">
                                                    <td className="p-3.5">
                                                        <div className="font-semibold text-slate-800 dark:text-slate-100">
                                                            {entry.provider?.name || reportData?.provider?.name || 'Unknown'}
                                                        </div>
                                                        <div className="text-[11px] text-slate-400">
                                                            {entry.provider?.service_type || reportData?.provider?.service_type}
                                                        </div>
                                                    </td>
                                                    <td className="p-3.5 font-medium">{unitNumber}</td>
                                                    <td className="p-3.5 font-mono text-slate-600 dark:text-slate-300">{entryFormatted}</td>
                                                    <td className="p-3.5 font-mono text-slate-600 dark:text-slate-300">
                                                        {isInside ? <span className="text-sky-500 font-sans italic">Inside</span> : exitFormatted}
                                                    </td>
                                                    <td className="p-3.5 text-slate-600 dark:text-slate-300">
                                                        {entry.security_user?.name || 'Security Guard'}
                                                    </td>
                                                    <td className="p-3.5 text-slate-500 dark:text-slate-400">
                                                        {entry.remarks || '-'}
                                                    </td>
                                                    <td className="p-3.5 text-right">
                                                        <Badge color={isInside ? 'blue' : 'green'} sm>
                                                            {isInside ? t("status_inside") : t("stat_completed_visits")}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {(!reportData?.entries?.length && !reportData?.attendance?.length && !reportLoading) && (
                            <div className="p-10 text-center text-slate-400 text-xs">
                                No records found for the selected criteria.
                            </div>
                        )}
                    </Card>
                </div>
            )}

            {/* Check-in with optional remarks modal */}
            {checkInProvider && (
                <Modal 
                    title={`${t("btn_check_in")}: ${checkInProvider.name}`} 
                    onClose={() => {
                        setCheckInProvider(null);
                        setCheckInRemarks('');
                    }}
                >
                    <div className="space-y-4">
                        <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-xl text-xs text-emerald-800 dark:text-emerald-300">
                            <p className="font-semibold mb-0.5">✅ {checkInProvider.name} (ID #{checkInProvider.id})</p>
                            <p>{checkInProvider.service_type} • {checkInProvider.mobile_number}</p>
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                {t("label_optional_remarks")}
                            </label>
                            <textarea
                                rows={2}
                                value={checkInRemarks}
                                onChange={(e) => setCheckInRemarks(e.target.value)}
                                placeholder={t("placeholder_optional_remarks")}
                                className="w-full p-2.5 text-xs rounded-xl border border-slate-200 dark:border-slate-700 dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/50 text-slate-900 dark:text-white"
                            />
                        </div>

                        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                            <Button 
                                variant="default" 
                                onClick={() => {
                                    setCheckInProvider(null);
                                    setCheckInRemarks('');
                                }}
                            >
                                {t("btn_cancel")}
                            </Button>
                            <Button 
                                variant="solid_success" 
                                loading={actionLoading}
                                onClick={() => handleCheckIn(checkInProvider, false, checkInRemarks.trim())}
                            >
                                {t("btn_check_in")}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {selectedPhoto && (
                <Modal title={t("title_photo_verification")} onClose={() => setSelectedPhoto(null)}>
                    <div className="flex justify-center p-4">
                        <img src={selectedPhoto} alt="Provider" className="max-w-full h-auto max-h-[70vh] rounded border border-slate-200 dark:border-slate-700 shadow-lg" />
                    </div>
                </Modal>
            )}

            {showHistoryModal && (
                <Modal 
                    title={t("title_all_recent_searches")} 
                    onClose={() => setShowHistoryModal(false)}
                >
                    <div className="space-y-4">
                        {/* Subheader with count and clear button */}
                        <div className="flex justify-between items-center pb-3 border-b border-slate-200/80 dark:border-slate-800">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                    {t("recent_searches")}
                                </span>
                                <span className="px-2 py-0.5 text-xs font-mono font-medium rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                                    {recentSearches.length}
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    clearRecentSearches();
                                    setShowHistoryModal(false);
                                }}
                                className="inline-flex items-center gap-1 text-xs font-medium text-rose-500 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 px-2 py-1 rounded-lg transition-colors cursor-pointer"
                            >
                                <Icon name="trash" size={13} />
                                {t("clear_history")}
                            </button>
                        </div>
                        
                        {/* Items grid / pill layout with search icon and hover effects */}
                        <div className="flex flex-wrap gap-2.5 max-h-[50vh] overflow-y-auto p-1">
                            {recentSearches.map((item, idx) => (
                                <div
                                    key={idx}
                                    className="group inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 bg-slate-50 dark:bg-slate-800/90 hover:bg-primary/10 hover:border-primary/40 dark:hover:bg-primary/20 dark:hover:border-primary/50 text-slate-700 dark:text-slate-200 rounded-xl border border-slate-200 dark:border-slate-700/80 text-xs font-medium transition-all duration-150 shadow-sm"
                                >
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowHistoryModal(false);
                                            handleSearch(item);
                                        }}
                                        className="inline-flex items-center gap-2 cursor-pointer text-left hover:text-primary dark:hover:text-primary-light"
                                    >
                                        <Icon name="search" size={12} className="text-slate-400 group-hover:text-primary transition-colors" />
                                        <span>{item}</span>
                                    </button>
                                    <button
                                        type="button"
                                        title="Remove"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const updated = recentSearches.filter((_, i) => i !== idx);
                                            setRecentSearches(updated);
                                            try {
                                                localStorage.setItem('gate_provider_recent_searches', JSON.stringify(updated));
                                            } catch (err) {}
                                            if (updated.length === 0) setShowHistoryModal(false);
                                        }}
                                        className="w-5 h-5 flex items-center justify-center rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/50 transition-colors cursor-pointer"
                                    >
                                        <Icon name="x" size={11} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </Modal>
            )}

            {/* Manual Override Confirmation Modal */}
            {overrideProvider && (
                <Modal 
                    title={t("title_manual_override")} 
                    onClose={() => {
                        setOverrideProvider(null);
                        setOverrideReason('');
                    }}
                >
                    <div className="space-y-4">
                        <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl text-xs text-amber-800 dark:text-amber-300">
                            <p className="font-semibold mb-1">⚠️ {overrideProvider.name} (ID #{overrideProvider.id})</p>
                            <p>{overrideProvider.access_status?.reason || 'Access outside schedule or expired.'}</p>
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                {t("label_override_reason")} <span className="text-rose-500">*</span>
                            </label>
                            <textarea
                                rows={3}
                                required
                                value={overrideReason}
                                onChange={(e) => setOverrideReason(e.target.value)}
                                placeholder={t("placeholder_override_reason")}
                                className="w-full p-2.5 text-xs rounded-xl border border-slate-200 dark:border-slate-700 dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/50 text-slate-900 dark:text-white"
                            />
                        </div>

                        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                            <Button 
                                variant="default" 
                                onClick={() => {
                                    setOverrideProvider(null);
                                    setOverrideReason('');
                                }}
                            >
                                {t("btn_cancel")}
                            </Button>
                            <Button 
                                variant="solid_danger" 
                                loading={actionLoading}
                                disabled={!overrideReason.trim()}
                                onClick={handleConfirmOverride}
                            >
                                {t("btn_confirm_entry")}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default SecurityGateSearch;
